import React, { useMemo } from 'react';
import RateDisclosures from '../RateDisclosures';
import Spinner from '@components/Spinner';
import CalculatorMessages from '@components/Content/CalculatorMessages';
import useConfig from '@hooks/useConfig';
import * as styles from './RackRates.module.scss';
import classNames from 'classnames';
import { loanPurpose, closingCostType } from '@enums';
import { formatPercent, formatCurrency, isValidDate } from '@helpers';

export default function RackRates({
  groups = [],
  currentLoanPurpose = loanPurpose.REFINANCE,
  currentLoanType = closingCostType.NCC,
  rackRatesAreLoading,
  rackRatesError,
  setModalProduct
}) {
  const config = useConfig();
  const { components, effectiveDate } = useMemo(() => {
    if (!groups?.length) {
      return { components: null, effectiveDate: null };
    }

    let effectiveDate = null;

    const components = groups
      .map((group, index) => {
        const isNCC = currentLoanType === closingCostType.NCC;
        const isPoints = currentLoanType === closingCostType.POINTS;

        if (
          group.loanPurpose !== currentLoanPurpose ||
          !isValidDate(group.effectiveDate) ||
          (!isNCC && !isPoints)
        ) {
          return null;
        }

        const date = new Date(group.effectiveDate);

        if (!effectiveDate || date < effectiveDate) {
          effectiveDate = date;
        }

        return (
          <RackRateGroup
            key={index}
            title={group.title}
            products={isNCC ? group.nccRates : group.ptsFeesRates}
            setModalProduct={setModalProduct}
          />
        );
      })
      .filter(g => g);

    return { components, effectiveDate };
  }, [groups, currentLoanType, currentLoanPurpose, setModalProduct]);

  const content =
    rackRatesAreLoading || (components?.length && !rackRatesError) ? (
      components
    ) : (
      <div className={styles.messageWrapper}>
        <CalculatorMessages
          className={styles.messages}
          errors={!rackRatesError ? null : { general: rackRatesError }}
          messageTitle="No mortgage rates found"
          messageText={config.noRackRatesMessage}
          showRackRatesInvitation={false}
        />
      </div>
    );

  return (
    <>
      <div
        className={classNames('section', 'content-wrapper', styles.wrapper, {
          [styles.loading]: rackRatesAreLoading
        })}
      >
        {rackRatesAreLoading ? <Spinner /> : content}
      </div>
      <RateDisclosures effectiveDate={effectiveDate} />
    </>
  );
}

function RackRateGroup({ title, products, setModalProduct }) {
  return !products?.length ? null : (
    <div className={styles.group}>
      {title && <div className={styles.title}>{title}</div>}
      <div className={styles.table}>
        <div className={classNames(styles.row, styles.header)}>
          <HeaderCell>Product &amp; Term</HeaderCell>
          <HeaderCell>
            Interest Rate <span className={styles.slash}>/</span> APR
          </HeaderCell>
          <HeaderCell>Points</HeaderCell>
          <HeaderCell>Monthly Payment</HeaderCell>
        </div>
        {products.map((product, index) => (
          <Row
            key={index}
            product={product}
            setModalProduct={setModalProduct}
          />
        ))}
      </div>
    </div>
  );
}

function Row({ product, setModalProduct }) {
  const { productLabel, rate, apr, closingCosts, payment, isNCC } = product;

  let pointsComponent = null;

  if (isNCC || closingCosts === 0) {
    pointsComponent = '$0.00';
  } else {
    const isPositive = closingCosts > 0;
    const sign = isPositive ? '+' : '-';
    const points = formatCurrency(Math.abs(closingCosts));

    pointsComponent = (
      <>
        <span
          className={classNames(styles.sign, {
            [styles.negative]: !isPositive
          })}
        >
          {sign}
        </span>
        {points}
      </>
    );
  }

  return (
    <div
      className={classNames(styles.row, styles.product)}
      tabIndex={0}
      role="button"
      aria-controls="calculator-rate-modal"
      onClick={() => setModalProduct(product)}
    >
      <Cell>{productLabel}</Cell>
      <Cell className={styles.rate}>
        <span className={styles.interestRate}>
          <Label>Interest Rate:</Label> <Percent value={rate} />
        </span>{' '}
        <span className={styles.slash}>/</span>{' '}
        <span className={styles.apr}>
          <Label>APR:</Label> <Percent value={apr} />
        </span>
      </Cell>
      <Cell label="Points:">{pointsComponent}</Cell>
      <Cell label="Monthly Payment:">{formatCurrency(payment)}</Cell>
    </div>
  );
}

function Cell({ label, className, children }) {
  return (
    <div className={classNames(styles.cell, className)}>
      {label && <Label>{label}</Label>} {children && <span>{children}</span>}
    </div>
  );
}

function HeaderCell({ className, ...rest }) {
  return (
    <Cell
      className={classNames(styles.headerCell, className)}
      {...rest}
    />
  );
}

function Label({ children }) {
  return !children ? null : <span className={styles.label}>{children}</span>;
}

function Percent({ value }) {
  return (
    <>
      {formatPercent(value, true, false)}
      <span className={styles.percent}>%</span>
    </>
  );
}
